import type { ZodIssue } from "zod";

interface QueryWithError {
  error: Error | null;
  validationError: ZodIssue[] | null;
}

interface CombinedErrorOptions {
  /** Custom prefix for the error message. If not provided, uses generic message */
  errorPrefix?: string;
}

/**
 * Combines query errors and validation errors from a single cube query into a single Error object
 * @param query - The query result with error and validationError properties
 * @param options - Optional configuration for error message formatting
 * @returns Combined error or null if no errors exist
 */
export function createCombinedError(
  query: QueryWithError,
  options?: CombinedErrorOptions,
): Error | null {
  const { errorPrefix } = options ?? {};

  if (query.error) {
    return query.error;
  }

  if (query.validationError) {
    const prefix = errorPrefix ?? "Query validation failed";
    const messages = query.validationError.map((e) => e.message).join(", ");
    return new Error(`${prefix}: ${messages}`);
  }

  return null;
}

/**
 * Combines errors from multiple cube queries into a single Error object
 * Returns the first error encountered in the order: query errors, then validation errors
 * @param queries - Array of query results with their optional error prefixes
 * @returns Combined error or null if no errors exist
 */
export function createCombinedErrorFromMultiple(
  queries: (QueryWithError & CombinedErrorOptions)[],
): Error | null {
  // First check for any query errors
  for (const query of queries) {
    if (query.error) {
      return query.error;
    }
  }

  // Then check for validation errors
  for (const query of queries) {
    if (query.validationError) {
      const prefix = query.errorPrefix ?? "Query validation failed";
      const messages = query.validationError.map((e) => e.message).join(", ");
      return new Error(`${prefix}: ${messages}`);
    }
  }

  return null;
}
